import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Shield, AlertCircle, Clock, Mail, CheckCircle, X } from 'lucide-react';
import { BRAND_FULL_NAME } from '../config/brand';

const RefundPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-gray-500 mb-4" dir="ltr">
            <Link to="/" className="hover:text-green-600 transition-colors duration-200 flex items-center">
              الرئيسية
              <ArrowRight className="w-4 h-4 mx-2" />
            </Link>
            <span className="text-gray-900">سياسة الاسترجاع</span>
          </nav>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-green-600" />
            سياسة الاسترجاع
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 lg:p-10">
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              في <span className="font-bold text-green-600">{BRAND_FULL_NAME}</span>، نقدم دورات تعليمية رقمية لمختلف المراحل الدراسية. 
              يتم تسليم جميع المحتويات عبر الإنترنت وتصبح متاحة فوراً بعد الشراء. لهذا السبب، نطبق القواعد التالية للاسترجاع 
              لضمان العدالة لكل من الطلاب والمعلمين.
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 font-bold text-xl">1</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">سياسة المنتج الرقمي</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                جميع الدورات المباعة على {BRAND_FULL_NAME} هي منتجات رقمية (دروس فيديو، ملفات PDF، اختبارات، ومواد تعليمية).
              </p>
              <p className="text-gray-700 leading-relaxed">
                بمجرد حصول الطالب على الوصول إلى الدورة، يُعتبر المحتوى مستخدماً.
              </p>
              <p className="text-gray-700 leading-relaxed font-semibold text-green-700">
                لهذا السبب، الاسترجاع محدود.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 font-bold text-xl">2</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                متى يُسمح بالاسترجاع
              </h2>
            </div>
            <div className="bg-green-50 rounded-lg p-6 border-r-4 border-green-600">
              <p className="text-gray-700 mb-4 font-semibold">يمكن إصدار استرجاع فقط في الحالات التالية:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>لا يمكن الوصول إلى الدورة بسبب مشكلة تقنية من جانبنا وفشلنا في إصلاحها خلال 48 ساعة.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>تم تحصيل رسوم من الطالب بشكل غير صحيح (دفعة مكررة أو دورة خاطئة).</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>محتوى الدورة مختلف تماماً عما تم وصفه على صفحة الدورة.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-xl">3</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <X className="w-6 h-6 text-red-600" />
                متى لا يُسمح بالاسترجاع
              </h2>
            </div>
            <div className="bg-red-50 rounded-lg p-6 border-r-4 border-red-600">
              <p className="text-gray-700 mb-4 font-semibold">لن يتم توفير استرجاع إذا:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>شاهد الطالب أي جزء من الدورة.</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>قام الطالب بتحميل أي مواد.</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>ادعى الطالب أنه لم يعجبه أسلوب التدريس.</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>لم يكن لدى الطالب وقت للدراسة.</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>اشترى الطالب الصف أو المادة الخاطئة عن طريق الخطأ.</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>تم شراء الدورة خلال خصم أو عرض ترويجي.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xl">4</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-600" />
                كيفية طلب الاسترجاع
              </h2>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 border-r-4 border-blue-600">
              <p className="text-gray-700 mb-4 font-semibold">لطلب الاسترجاع، يجب على الطالب:</p>
              <ul className="space-y-3 text-gray-700 mb-4">
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>التواصل مع دعم {BRAND_FULL_NAME} خلال <span className="font-bold">24 ساعة</span> من الشراء.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>تقديم المعلومات التالية:</span>
                </li>
              </ul>
              <div className="bg-white rounded-lg p-4 mt-4 space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>البريد الإلكتروني المسجل</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>اسم الدورة</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>مرجع الدفع أو الإيصال</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>سبب الطلب</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-semibold">
                  ⚠️ لن يتم النظر في الطلبات المرسلة بعد 24 ساعة.
                </p>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 font-bold text-xl">5</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-6 h-6 text-purple-600" />
                معالجة الاسترجاع
              </h2>
            </div>
            <div className="bg-purple-50 rounded-lg p-6 border-r-4 border-purple-600">
              <p className="text-gray-700 mb-4 font-semibold">في حالة الموافقة:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>سيتم معالجة الاسترجاع خلال <span className="font-bold">7-14 يوم عمل</span></span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>سيتم إرجاع الأموال باستخدام طريقة الدفع الأصلية</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 font-bold text-xl">6</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-orange-600" />
                حماية من الاحتيال
              </h2>
            </div>
            <div className="bg-orange-50 rounded-lg p-6 border-r-4 border-orange-600">
              <p className="text-gray-700 mb-4">
                إذا اكتشفنا إساءة استخدام نظام الاسترجاع (طلبات استرجاع متعددة، استخدام المحتوى قبل الاسترجاع، أو ادعاءات كاذبة)، 
                تحتفظ {BRAND_FULL_NAME} بالحق في:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span>رفض طلبات الاسترجاع المستقبلية</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span>تعليق الحساب</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Section 7 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-600 font-bold text-xl">7</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">الاتفاقية</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border-r-4 border-gray-600">
              <p className="text-gray-700 leading-relaxed font-semibold">
                بشراء أي دورة على {BRAND_FULL_NAME}، يوافق الطالب على سياسة الاسترجاع هذه.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-10 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              للتواصل مع الدعم
            </h3>
            <p className="text-gray-700 mb-4">
              إذا كان لديك أي استفسارات حول سياسة الاسترجاع أو تحتاج إلى طلب استرجاع، يرجى التواصل معنا.
            </p>
            <Link
              to="/"
              className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
            >
              العودة للصفحة الرئيسية
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
